import { UseQueryResult } from '@tanstack/react-query';

import {
  CountyView,
  DeliveryAddressSearchResponse,
  FanStreetData,
  LocalityView,
  PageResponsePickupOrderResponse,
  PageResponseShipmentResponse,
  PickupOrderResponse,
  PickupOrderSearchRequest,
  PickupPointView,
  ServiceOptionView,
  ServiceView,
  ShipmentResponse,
  ShipmentSearchRequest,
  ShipmentTrackingEventResponse,
} from '@/api/data-contracts';

import { DeliveryAddressSearchParams, useShippingService } from './shipping.service';
import { nomenclatorApi, shippingApi } from './shippingApi';

const DAY = 24 * 60 * 60 * 1000;
const HOUR = 60 * 60 * 1000;

export const useCountiesQuery = (enabled = true): UseQueryResult<CountyView[]> => {
  const { listCounties } = useShippingService();

  return nomenclatorApi.useFeatureQuery<CountyView[]>({
    queryKey: nomenclatorApi.keys.sub('counties'),
    queryFn: (signal) => listCounties(signal),
    staleTime: DAY,
    cacheTime: DAY,
    enabled,
  });
};

export const useLocalitiesQuery = (
  county: string | undefined,
  enabled = true,
): UseQueryResult<LocalityView[]> => {
  const { listLocalities } = useShippingService();

  return nomenclatorApi.useFeatureQuery<LocalityView[]>({
    queryKey: nomenclatorApi.keys.sub('localities', county ?? ''),
    queryFn: (signal) => listLocalities(county!, undefined, signal),
    staleTime: HOUR,
    cacheTime: HOUR,
    enabled: enabled && !!county,
  });
};

export const useStreetsQuery = (
  county: string | undefined,
  locality: string | undefined,
  enabled = true,
): UseQueryResult<FanStreetData[]> => {
  const { listStreets } = useShippingService();

  return nomenclatorApi.useFeatureQuery<FanStreetData[]>({
    queryKey: nomenclatorApi.keys.sub('streets', county ?? '', locality ?? ''),
    queryFn: (signal) => listStreets(county!, locality!, undefined, signal),
    staleTime: HOUR,
    cacheTime: HOUR,
    enabled: enabled && !!county && !!locality,
  });
};

export const useCourierServicesQuery = (enabled = true): UseQueryResult<ServiceView[]> => {
  const { listCourierServices } = useShippingService();

  return nomenclatorApi.useFeatureQuery<ServiceView[]>({
    queryKey: nomenclatorApi.keys.sub('courier-services'),
    queryFn: (signal) => listCourierServices(signal),
    staleTime: 0,
    enabled,
  });
};

export const useServiceOptionsQuery = (
  service: string | undefined,
  clientId: string | undefined,
  enabled = true,
): UseQueryResult<ServiceOptionView[]> => {
  const { listServiceOptions } = useShippingService();

  return nomenclatorApi.useFeatureQuery<ServiceOptionView[]>({
    queryKey: nomenclatorApi.keys.sub('service-options', service ?? '', clientId ?? ''),
    queryFn: (signal) => listServiceOptions(service!, clientId, signal),
    staleTime: HOUR,
    cacheTime: HOUR,
    enabled: enabled && !!service,
  });
};

export const usePickupPointsQuery = (enabled = true): UseQueryResult<PickupPointView[]> => {
  const { listPickupPoints } = useShippingService();

  return shippingApi.useFeatureQuery<PickupPointView[]>({
    queryKey: shippingApi.keys.sub('pickup-points'),
    queryFn: (signal) => listPickupPoints(signal),
    enabled,
  });
};

export const useDeliveryAddressesQuery = (
  params: DeliveryAddressSearchParams,
  enabled = true,
): UseQueryResult<DeliveryAddressSearchResponse> => {
  const { searchDeliveryAddresses } = useShippingService();

  return shippingApi.useFeatureQuery<DeliveryAddressSearchResponse>({
    queryKey: shippingApi.keys.sub('delivery-addresses', params),
    queryFn: (signal) => searchDeliveryAddresses(params, signal),
    staleTime: 0,
    keepPreviousData: true,
    enabled,
  });
};

export const useTaskShipmentsQuery = (
  taskId: string | undefined,
  enabled = true,
  refetchInterval: ((data: ShipmentResponse[] | undefined) => number | false) | false = false,
): UseQueryResult<ShipmentResponse[]> => {
  const { listTaskShipments } = useShippingService();

  return shippingApi.useFeatureQuery<ShipmentResponse[]>({
    queryKey: shippingApi.keys.sub('task-shipments', taskId ?? ''),
    queryFn: (signal) => listTaskShipments(taskId!, signal),
    staleTime: 0,
    enabled: enabled && !!taskId,
    refetchInterval,
  });
};

export const usePickupOrdersQuery = (
  request: PickupOrderSearchRequest,
  enabled = true,
): UseQueryResult<PageResponsePickupOrderResponse> => {
  const { searchPickupOrders } = useShippingService();

  return shippingApi.useFeatureQuery<PageResponsePickupOrderResponse>({
    queryKey: shippingApi.keys.sub('pickup-orders', request),
    queryFn: (signal) => searchPickupOrders(request, signal),
    staleTime: 0,
    enabled,
  });
};

export const usePendingPickupsQuery = (enabled = true): UseQueryResult<PickupOrderResponse[]> => {
  const { listPendingPickups } = useShippingService();

  return shippingApi.useFeatureQuery<PickupOrderResponse[]>({
    queryKey: shippingApi.keys.sub('pending-pickups'),
    queryFn: (signal) => listPendingPickups(signal),
    staleTime: 0,
    enabled,
  });
};

export const useShipmentQuery = (
  id: string | undefined,
  enabled = true,
): UseQueryResult<ShipmentResponse> => {
  const { getShipment } = useShippingService();

  return shippingApi.useFeatureQuery<ShipmentResponse>({
    queryKey: shippingApi.keys.detail(id ?? ''),
    queryFn: (signal) => getShipment(id!, signal),
    staleTime: 0,
    enabled: enabled && !!id,
  });
};

export const useShipmentsSearchQuery = (
  request: ShipmentSearchRequest,
  enabled = true,
): UseQueryResult<PageResponseShipmentResponse> => {
  const { searchShipments } = useShippingService();

  return shippingApi.useFeatureQuery<PageResponseShipmentResponse>({
    queryKey: shippingApi.keys.list(request),
    queryFn: (signal) => searchShipments(request, signal),
    staleTime: 0,
    keepPreviousData: true,
    enabled,
  });
};

export const useShipmentTrackingQuery = (
  id: string | undefined,
  enabled = true,
): UseQueryResult<ShipmentTrackingEventResponse[]> => {
  const { getShipmentTracking } = useShippingService();

  return shippingApi.useFeatureQuery<ShipmentTrackingEventResponse[]>({
    queryKey: shippingApi.keys.sub('tracking', id ?? ''),
    queryFn: (signal) => getShipmentTracking(id!, signal),
    staleTime: 0,
    enabled: enabled && !!id,
  });
};
